// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

// G7-N1（2026-10-09）：用户澄清选择气泡的单一派生源。
//
// live（`ui.clarification_cards` 经只读适配器）与恢复（checkpoint 同名通道）
// 两条路径都经 `clarificationChoiceBubbleFromCard` 派生同 id/同内容的投影
// 条目——同源派生是防 #185 式 live↔恢复振荡的结构约束，禁止在任何一侧另写
// 一份派生逻辑。

export interface ClarificationChoiceLabels {
  question: string;
  answer: string;
}

/** i18n 标签入参（调用方经 useI18n/locales 供给，数据层不硬编码文案）。 */
export function derivationLabels(
  question: string,
  answer: string
): ClarificationChoiceLabels {
  return { question, answer };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** 选项 chip 的展示 label（string 选项原样；对象取 label→value 兜底）。 */
export function clarificationOptionLabel(option: unknown): string {
  if (typeof option === 'string') return option;
  const record = asRecord(option);
  if (typeof record.label === 'string' && record.label) return record.label;
  if (typeof record.value === 'string') return record.value;
  return '';
}

/** 选项 chip 的落账 value（string 选项原样；对象取 value→label 兜底）。 */
export function clarificationOptionValue(option: unknown): string {
  if (typeof option === 'string') return option;
  const record = asRecord(option);
  if (typeof record.value === 'string' && record.value) return record.value;
  if (typeof record.label === 'string') return record.label;
  return '';
}

/** 答案落账存的是选项 value（澄清卡回显同一原则）：还原为人类可读 label；
 * 自定义作答无对应选项时原样回显。

 * UI-005：多选答案落账为 value 数组 → 逐项映射 label 后用 `、` 连接（展示
 * 用途）；此前数组答案被当作非字符串静默丢弃，历史气泡不渲染。空数组 =
 * 未作答（返回空串）。 */
export function clarificationAnswerLabel(question: {
  answer?: unknown;
  options?: unknown;
}): string {
  const raw = question.answer;
  const options = Array.isArray(question.options) ? question.options : [];
  const labelFor = (value: string): string => {
    for (const option of options) {
      if (clarificationOptionValue(option) === value) {
        return clarificationOptionLabel(option) || value;
      }
    }
    return value;
  };
  if (Array.isArray(raw)) {
    return raw
      .map((item) => (typeof item === 'string' ? item : String(item ?? '')))
      .filter((item) => item)
      .map(labelFor)
      .join('、');
  }
  const answer = typeof raw === 'string' ? raw : '';
  if (!answer) return '';
  return labelFor(answer);
}

export interface ClarificationChoiceBubble {
  /** 确定性 id（`clarify-choice-<int submitted_at>`）：live/恢复同 id 幂等。 */
  id: string;
  content: string;
  submittedAtMs: number;
}

/** 卡片 payload → 用户选择气泡。无可读答案（未作答/空卡）返回 null。 */
export function clarificationChoiceBubbleFromCard(
  card: unknown,
  labels: ClarificationChoiceLabels
): ClarificationChoiceBubble | null {
  const record = asRecord(card);
  const rawSubmittedAt = Number(record.submitted_at);
  const submittedAtMs = Number.isFinite(rawSubmittedAt)
    ? Math.trunc(rawSubmittedAt) * 1000
    : 0;
  const id = `clarify-choice-${
    Number.isFinite(rawSubmittedAt) ? Math.trunc(rawSubmittedAt) : 'unknown'
  }`;

  const questions = Array.isArray(record.questions) ? record.questions : [];
  const lines: string[] = [];
  for (const rawQuestion of questions) {
    const question = asRecord(rawQuestion);
    const answer = clarificationAnswerLabel(question);
    if (!answer) continue;
    const prompt = typeof question.prompt === 'string' ? question.prompt.trim() : '';
    if (prompt) {
      lines.push(`${labels.question}: ${prompt}`);
    }
    lines.push(`${labels.answer}: ${answer}`);
  }
  if (lines.length === 0) {
    return null;
  }
  return { id, content: lines.join('\n'), submittedAtMs };
}

/** 中断已答复证据：`ui.control_events` 链中存在 interrupt_resumed 事件
 * （G7-N1 一致性对账信号：卡片存在但无 resumed 证据 = 中断仍未答，
 * 不渲染选择气泡）。 */
export function hasInterruptResumedEvidence(controlEvents: unknown): boolean {
  if (!Array.isArray(controlEvents)) {
    return false;
  }
  return controlEvents.some((event) => {
    const record = asRecord(event);
    const eventType =
      typeof record.event_type === 'string'
        ? record.event_type
        : typeof record.type === 'string'
          ? record.type
          : '';
    return eventType === 'interrupt_resumed';
  });
}
